import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { RoundSection } from "../challenges/RoundSection";
import { MetricStats } from "./MetricStats";
import { EntryRow } from "./Journal";
import { BuzerIcon } from "../../BuzerIcons";
import { Icon } from "../../icons";
import { Glyph, Timer } from "../../components/ui";
import { Sheet, Button, Caption, IconButton, DescrBox, DifficultyTag, SectionHeader } from "../../components/ui/ds";
import { fmtMetric, freqLabel, streakLabel } from "../../lib/format";
import { fmtDuePretty } from "../../lib/date-sk";
import { LIFE_CAT, METRIC_PERIOD_SHORT, RECUR_PERIOD_UNIT } from "../../lib/constants";
import { iconChipStyle } from "../../lib/icons-resolve";

// Reusable task/habit detail sheet ("Detail úlohy"). Built on the Sheet overlay
// primitive; opened by tapping a habit row (and reusable for any task-like item).
// Renders the stats we actually have (points, frequency, 7-day history + weekly
// progress) and the primary actions. All behaviour comes in via props so the
// same component serves habits, challenges, etc.
// tone = odtieň pilulky; ak sa neposkytne, odvodí sa z valueColor
// (var(--brand-primary) → brand, var(--streak) → streak, inak neutral).
export function Stat({ icon, value, valueColor, label, tone }) {
  const t = tone || (valueColor === "var(--brand-primary)" ? "brand" : valueColor === "var(--streak)" ? "streak" : "neutral");
  return (
    <div className={`bz-tile bz-tile--${t}`}>
      <span className="bz-tile__icon">{icon}</span>
      <span className="bz-tile__value" style={valueColor ? { color: valueColor } : undefined}>{value}</span>
      <span className="bz-tile__label">{label}</span>
    </div>
  );
}

// v35 — bublina „prvý (neúplný) týždeň" (Plán A): keď je týždenný cieľ znížený (založené v priebehu
// týždňa / cez víkend), krátko vysvetlí, prečo je nižší a že od pondelka platí plný. Zdieľané detailom
// návyku aj výzvy (rovnaký vzhľad + copy). Nič neukáže, keď cieľ nie je znížený (target ≥ full).
export function FirstWeekNote({ target, full, style }) {
  if (!(target < full)) return null;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 12.5, color: "var(--neutral-text-muted)", lineHeight: 1.45, marginTop: 10, ...style }}>
      <BuzerIcon name="bulb" size={14} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: 1 }} />
      <span>Tento prvý (neúplný) týždeň stačí <strong>{target}×</strong> — začalo sa v priebehu týždňa, aby to nezačalo zlyhaním. Od pondelka platí plných <strong>{full}×</strong> za týždeň.</span>
    </div>
  );
}

// v29 — tlačidlo „Zhodnotiť s AI" a jeho výsledkový modal boli z detailu ODSTRÁNENÉ
// (rozhodnutie majiteľky — dočasné skrytie). Backend `/api/ai/evaluate-habit`
// (`ai.evaluate_habit`) ostáva nedotknutý, takže návrat je len o vrátenie tohto UI.

export function TaskDetail({ task, routineName, lite, onClose, onToggle, onSkip, onEdit, onDelete, onAmount, onJournal, onJournalWrite, onEditEntry, journalKey = 0 }) {
  const t = task;
  // DENNÍK §7 — posledné 3 zápisy priradené k návyku (do /api/state nepatria, dotiahnu sa
  // až pri otvorení detailu, ako kolá)
  const [jr, setJr] = useState(null);
  useEffect(() => {
    let alive = true;
    api("/api/journal?task_id=" + t.id + "&limit=3")
      .then((r) => { if (alive) setJr(r); })
      .catch(() => { if (alive) setJr({ days: [], total_entries: 0 }); });
    return () => { alive = false; };
  }, [t.id, journalKey]);
  const jEntries = jr ? jr.days.flatMap((d) => d.entries).slice(0, 3) : [];
  const jCount = jr ? jr.days.reduce((n, d) => n + d.entries.length, 0) : 0;
  const entryGoal = Number(t.entry_goal) || 0;
  // v33 §7 — KOLÁ: dopýtame si ich až pri otvorení detailu (do /api/state nepatria, poluje sa
  // každých 45 s). Prázdna odpoveď = návyk na nič nenadväzuje → nevykreslí sa nič.
  const [rnd, setRnd] = useState(null);
  useEffect(() => {
    let alive = true;
    api("/api/tasks/" + t.id + "/rounds")
      .then((r) => { if (alive) setRnd(r && r.round_no > 1 ? r : null); })
      .catch(() => { if (alive) setRnd(null); });
    return () => { alive = false; };
  }, [t.id]);
  // font-unify — popis (t.desc) sa presunul zo skracovaného podnadpisu do čitateľného tela (bz-aitext);
  // podnadpis nechávame len na rutinu, aby sa dlhý popis neorezával na jeden riadok.
  const subtitle = routineName || null;
  const weekly = t.recur_mode === "weekly_count" || t.recur_mode === "period";  // v33b — „N× za obdobie" má rovnaký postup-riadok
  const periodUnit = t.recur_mode === "period" ? (RECUR_PERIOD_UNIT[t.recur_period] || "obdobie") : "týždeň";
  const isDaily = (t.recur_mode || "daily") === "daily";  // daily habits skip the progress row + rule
  const remain = Math.max(0, (t.week_target || 0) - (t.week_done || 0));
  // v29 §3 — séria má už len dve jednotky: 'day' („5 dní") a 'times' („5×")
  const streakTxt = streakLabel(t.streak || 0, t.streak_unit);

  return (
    <Sheet center
      icon={<Glyph icon={t.icon} size={20} fallback="target" />} iconStyle={iconChipStyle(t.icon_color)}
      /* v29 §7.2 — náročnosť sedí POD veľkou ikonou (rovnaké miesto ako v miniatúre).
         Lite nemá body, takže tam hviezdičky nesvietia vôbec (§8.3). */
      title={t.label} subtitle={subtitle} onClose={onClose}
      footer={
        <div style={{ display: "flex", flexDirection: "column", gap: 9, width: "100%" }}>
          {/* Návyk, ktorý ešte NEZAČAL (sekcia „Začne o…"): plniť, preskakovať ani zapisovať
              hodnotu sa nedá — tie akcie patria dňu, ktorý ešte nenastal. Ostáva Upraviť
              a Vymazať, aby sa dal opraviť preklep v dátume alebo zrušiť úplne. */}
          {t.upcoming ? (
            <Caption icon="calendar">Začne {fmtDuePretty(t.start)} — dovtedy sa nedá plniť.</Caption>
          ) : (<>
          {/* merateľný návyk: splnenie sa neklikne, vyplýva zo zapísanej hodnoty — primárna
              akcia je preto zápis (aj keď je návyk už splnený, hodnota sa dá doplniť) */}
          {t.metric_label && onAmount && (
            <Button variant={t.done ? "ghost" : "primary"} block onClick={() => { onAmount(); onClose(); }}>
              <Icon name="edit" size={17} color="currentColor" />{t.done ? "Upraviť dnešnú hodnotu" : "Zapísať hodnotu"}
            </Button>
          )}
          {/* DENNÍK §8 — návyk „splní sa zápismi": fajka sa nekliká, primárna akcia je zápis */}
          {entryGoal > 0 && onJournalWrite && (
            <Button variant={t.done ? "ghost" : "primary"} block onClick={() => { onJournalWrite(); onClose(); }}>
              <BuzerIcon name="feather" size={17} color="currentColor" />{t.done ? "Dopísať do denníka" : `Napísať zápis (${Math.min(t.entries_today || 0, entryGoal)} z ${entryGoal})`}
            </Button>
          )}
          {entryGoal > 0 ? null : t.done ? (
            <Button variant="destructive" block onClick={(e) => { onToggle(e); onClose(); }}><BuzerIcon name="undo" size={16} color="currentColor" />Vrátiť splnenie</Button>
          ) : (
            <>
              {/* f-batch — posiela click event ďalej (onToggle(e)), nech splnenie z detailu
                  dostane ROVNAKÝ rozpŕskavajúci efekt v mieste ťuku ako riadok v zozname. */}
              {!(t.metric_label && onAmount) && !entryGoal && (
              <Button variant="primary" block onClick={(e) => { onToggle(e); onClose(); }}>
                <Icon name="check" size={19} color="currentColor" stroke={2.6} />{lite ? "Splniť" : `Splniť +${t.pts}`}
              </Button>
              )}
              {t.timer_sec > 0 && (
                <Timer variant="button" id={t.id} seconds={t.timer_sec} onComplete={() => { onToggle(); onClose(); }} />
              )}
              {/* v25 §4.4 — preskočiť dnes (ospravedlnený deň — séria sa nepreruší) */}
              {/* v25 §10.8 — zrušenie preskočenia je deštruktívne + potvrdzuje sa (parent otvorí confirm) */}
              {onSkip && (t.skip_today
                ? <Button variant="destructive" block onClick={() => { onSkip(false); onClose(); }}><BuzerIcon name="undo" size={16} color="currentColor" />Zrušiť preskočenie</Button>
                : <Button variant="ghost" block style={{ color: "var(--brand-primary)" }} title="Deň sa ospravedlní — séria sa nepreruší" onClick={() => { onSkip(true); onClose(); }}><BuzerIcon name="wave" size={16} color="currentColor" />Preskočiť dnes</Button>)}{/* v27 §7 — len text vo farbe témy, bez výplne */}
            </>
          )}
          </>)}
          {/* v2 — Upraviť + circled trash (⋯ menu retired) */}
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="ghost" block onClick={() => { onEdit(); onClose(); }}>
              <Icon name="edit" size={16} color="currentColor" />Upraviť
            </Button>
            {onDelete && <IconButton variant="ghost" aria-label="Vymazať" title="Vymazať" onClick={() => { onDelete(); }} style={{ color: "var(--danger)" }}><Icon name="delete" size={16} color="currentColor" /></IconButton>}
          </div>
        </div>
      }
    >
      {/* font-unify → popis návyku v rovnakom ráme ako pri výzve/súťaži (zdieľaný DescrBox) */}
      <DescrBox text={t.desc} style={{ marginBottom: 10 }} />
      {/* v33 §7 — KOLÁ: keď návyk nadväzuje na archivovaný, ukáž percento splnenia minulého kola,
          métu a tieň. Rovnaká sekcia ako pri výzve a súťaži, len v percentách. */}
      {rnd && <RoundSection data={rnd} fmtVal={(v) => Math.round(v) + " %"} />}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {/* v29 §7.2 — náročnosť NAMIESTO bodovej dlaždice: body sú z nej odvodené, takže
            samostatná dlaždica „BODY" bola len duplicitná. „i" otvorí celé bodovanie (§7.3). */}
        {!lite && <DifficultyTag level={t.difficulty} streak={t.streak || 0} info />}
        <Stat icon={<BuzerIcon name="calendar" size={17} color="currentColor" />} value={freqLabel(t)} label="FREKV." />
        {/* v33h — Lite (bez bodov) má sériu vo farbe témy, nie streak-oranžovú */}
        {/* nezačatý návyk nemá sériu — „0 dní" by tam bolo len prázdne číslo */}
        {!t.upcoming && <Stat icon={<BuzerIcon name="flame" size={17} color={lite ? "var(--brand-primary)" : "var(--streak)"} />} value={streakTxt} valueColor={lite ? "var(--brand-primary)" : "var(--streak)"} label="SÉRIA" />}
        {/* v32 — štvrtá dlaždica: životná kategória */}
        {t.category && LIFE_CAT[t.category] && (
          <Stat icon={<BuzerIcon name={LIFE_CAT[t.category].icon} size={17} color="currentColor" />} value={LIFE_CAT[t.category].label} label="KATEGÓRIA" />
        )}
        {t.days_left != null && <Stat icon={<BuzerIcon name="hourglass" size={17} color="currentColor" />} value={`${t.days_left} dní`} label="AKTÍVNE EŠTE" />}
        {/* DENNÍK §8 — koľko zápisov má deň z cieľa (ráta sa každý zápis dňa) */}
        {entryGoal > 0 && (
          <Stat icon={<BuzerIcon name="feather" size={17} color="currentColor" />}
            value={`${Math.min(t.entries_today || 0, entryGoal)}/${entryGoal}`} label="ZÁPISY DNES" />
        )}
        {/* merateľný návyk („vlastný výsledok"): nazbierané v prebiehajúcom pokuse z cieľa.
            Pri „N× za obdobie" sa pokus prenáša cez polnoc, takže dlaždica nehlási „DNES",
            ale obdobie, na ktoré cieľ naozaj platí (TÝŽDEŇ/MESIAC/…). */}
        {t.metric_label && (
          <Stat icon={<BuzerIcon name={t.metric_time ? "stopwatch" : "target"} size={17} color="currentColor" />}
            value={(Number(t.metric_target) || 0) > 0
              ? `${fmtMetric(t.metric_today, t.metric_label, t.metric_time)} / ${fmtMetric(t.metric_target, t.metric_label, t.metric_time)}`
              : fmtMetric(t.metric_today, t.metric_label, t.metric_time)}
            label={METRIC_PERIOD_SHORT[t.metric_period] || "DNES"} />
        )}
        {/* v34 — dosiahnutá frekvencia ako pilulka (rovnaký font ako ostatné dlaždice): bodky sú „ikona", N/N je hodnota */}
        {weekly && (
          <Stat
            icon={<span style={{ display: "inline-flex", gap: 4 }}>
              {Array.from({ length: t.week_target || 0 }).map((_, i) => (
                <span key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: i < (t.week_done || 0) ? "var(--brand-primary)" : "transparent", border: i < (t.week_done || 0) ? "none" : "1.5px solid var(--neutral-border-strong)" }} />
              ))}
            </span>}
            value={`${t.week_done || 0}/${t.week_target}`}
            label="splnené"
          />
        )}
      </div>

      {/* v35 — rovnaká „prvý týždeň" bublina ako v detaile výzvy (len pri weekly_count, kde platí
          „N× za týždeň"); pri N× za mesiac/pol rok/rok by copy „od pondelka" nesedelo. */}
      {t.recur_mode === "weekly_count" && <FirstWeekNote target={t.week_target || 0} full={t.recur_n || 0} />}

      {/* postup pri intervalových/týždňových-dňových návykoch (nie „N× za obdobie") — dnešný stav */}
      {!isDaily && !weekly && (
        <div style={{ marginTop: 10, marginBottom: 6, display: "flex", alignItems: "center", gap: 9 }}>
          {t.done ? (<>
            <Icon name="check" size={16} color="var(--success)" stroke={2.4} /><span style={{ fontSize: 13, fontWeight: 700, color: "var(--success)" }}>dnes splnené · séria +1</span>
          </>) : (
            <span style={{ fontSize: 13, color: "var(--neutral-text-muted)" }}>Dnes ešte nesplnené</span>
          )}
        </div>
      )}

      {/* merateľný návyk — čísla (graf 30 dní + priemer/rekord/spolu/tento týždeň).
          Nezačatý návyk ich nemá odkiaľ mať, tak sa sekcia vôbec nepýta. */}
      {t.metric_label && !t.upcoming && <MetricStats task={t} />}

      {/* DENNÍK §7 — „Tvoje zápisy": posledné 3 zápisy priradené k návyku; chevron otvorí Celý
          denník s filtrom, „Pridať zápis" editor s predvyplneným návykom. Bez prázdneho stavu
          navyše — detail je už dlhý. */}
      {!t.upcoming && jr && (
        <div style={{ marginTop: 12 }}>
          <SectionHeader label="Tvoje zápisy" count={jr.total_entries ? jCount : 0} icon="feather"
            onClick={onJournal ? () => { onJournal(); onClose(); } : undefined} />
          {jEntries.length > 0 && (
            <div className="bz-journal__list" style={{ marginTop: 8 }}>
              {jEntries.map((e) => <EntryRow key={e.id} e={e} showDay onClick={() => onEditEntry && onEditEntry(e)} />)}
            </div>
          )}
          {onJournalWrite && (
            <Button variant="ghost" size="sm" style={{ marginTop: 8 }} onClick={() => { onJournalWrite(); onClose(); }}>
              <BuzerIcon name="add" size={14} color="currentColor" /> Pridať zápis
            </Button>
          )}
        </div>
      )}

      {/* v25 §8 — nastavené pripomenutie návyku */}
      {t.remind_at && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--neutral-text-muted)" }}>
          <BuzerIcon name="bell" size={14} color="currentColor" />Pripomenutie o {t.remind_at}
        </div>
      )}

    </Sheet>
  );
}
